import { useState, useEffect } from "react";
import { Accordion, Form, Button } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import axios from "axios";
import "../styles/filters.css";
import { useAuth } from "../context/AuthContext";

const FilterSidebar = ({
  filters = {
    status: [],
    state: [],
    city: [],
    ministry: [],
    category: [],
    organization: [],
    department: [],
    deadline: [],
    eligibility: []
  },
  setFilters = () => {},
  type = "active"
}) => {
  const { user, isAuthenticated } = useAuth();
  const [orgSearch, setOrgSearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [minSearch, setMinSearch] = useState("");
  const [catSearch, setCatSearch] = useState("");
  const [deptSearch, setDeptSearch] = useState("");

  const [filterData, setFilterData] = useState({
    dynamic: {
      state: [],
      ministry: [],
      department: [],
      organization: [],
      category: [],
      status: []
    },
    static: {
      deadline: [],
      eligibility: []
    },
    range: {
      tenderValue: { min: 0, max: 0, step: 0, displayUnit: "" },
      emdValue: { min: 0, max: 0, step: 0, displayUnit: "" }
    }
  });

  // Helper function to clean category names for display
  const cleanCategoryName = (category) => {
    if (!category) return category;
    return category
      .replace(/\s*Tenders$/, '')
      .replace(/\s*GlobalTenders$/, '')
      .trim();
  };

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const params = {
          type: type === 'reverse_auction' ? 'active' : type
        };

        if (user?.id) params.userId = user.id;
        
        const res = await axios.get(`${process.env.REACT_APP_BASE_API_URL}/api/tender-filters`, { params });
        
        const responseData = res.data?.data || {};
        
        const statuses = (responseData.status || []).filter(s =>
          s !== 'Favourites' || isAuthenticated
        );
        
        setFilterData({
          dynamic: {
            state: responseData.state || [],
            ministry: responseData.ministry || [],
            department: responseData.department || [],
            organization: responseData.organization || [],
            category: responseData.category || [],
            status: statuses
          },
          static: {
            deadline: responseData.static?.deadline || [],
            eligibility: responseData.static?.eligibility || []
          },
          range: {
            tenderValue: responseData.range?.tenderValue || { 
              min: 0, 
              max: 0, 
              step: 50000000, 
              displayUnit: "Cr" 
            },
            emdValue: responseData.range?.emdValue || { 
              min: 0, 
              max: 0, 
              step: 500000, 
              displayUnit: "Lakh" 
            }
          },
        });
      } catch (error) {
        console.error("Failed to fetch filters:", error);
        setFilterData({
          dynamic: {
            state: [],
            ministry: [],
            department: [],
            organization: [],
            category: [],
            status: []
          },
          static: {
            deadline: [],
            eligibility: []
          },
          range: {
            tenderValue: { min: 0, max: 0, step: 0, displayUnit: "" },
            emdValue: { min: 0, max: 0, step: 0, displayUnit: "" }
          }
        });
      }
    };
    fetchFilters();
  }, [type, user?.id, isAuthenticated]);

  const resetFilters = {
    status: [],
    state: [],
    city: [],
    ministry: [],
    department: [],
    category: [],
    organization: [],
    deadline: [],
    eligibility: [],
    minValue: null,
    emdValue: null
  };

  const handleResetFilters = () => {
    setFilters(resetFilters);
  };

  const toggleSelection = (key, value) => {
    setFilters(prev => {
      const current = prev[key] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [key]: exists ? current.filter(v => v !== value) : [...current, value]
      };
    });
  };

  const renderSearchableList = ({ data, search, setSearch, filterKey }) => {
    const filtered = (data || []).filter(item => 
      item && item.toLowerCase().includes((search || '').toLowerCase())
    );
    
    // Check if this is the category filter
    const isCategory = filterKey === 'category';
    
    return (
      <>
        <div className="ms-4 mb-1" style={{ fontSize: "12px", color: "#6c757d" }}>
          Total: <span className="fw-semibold text-dark">{(data || []).length}</span>
        </div>
        <div style={{ position: "relative", width: "80%" }} className="mb-3 ms-4">
          <FaSearch style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }} />
          <Form.Control 
            type="text" 
            placeholder="Search..." 
            value={search || ''} 
            onChange={(e) => setSearch(e.target.value)} 
            style={{ paddingLeft: "35px" }} 
          />
        </div>
        <div className="row" style={{ maxHeight: "250px", overflowY: "auto", margin: 0 }}>
          {filtered.map(item => {
            // For categories, show cleaned name but keep original value for filtering
            const displayName = isCategory ? cleanCategoryName(item) : item;
            
            return (
              <div className="col-12" key={item} style={{ padding: "2px 0" }}>
                <Form.Check 
                  type="checkbox"
                  id={`filter-${filterKey}-${item}`}
                  checked={(filters?.[filterKey] || []).includes(item)}
                  onChange={() => toggleSelection(filterKey, item)}
                  label={
                    <span 
                      style={{ 
                        wordBreak: 'break-word', 
                        display: 'inline-block',
                        fontSize: '14px',
                        lineHeight: '1.4',
                        paddingLeft: '4px'
                      }}
                    >
                      {displayName}
                    </span>
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '2px'
                  }}
                />
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && (
          <div className="text-center text-muted py-2" style={{ fontSize: '13px' }}>
            No matching {filterKey} found
          </div>
        )}
      </>
    );
  };

  const renderHeader = (title, filterKey, defaultVal = []) => {
    const isActive = Array.isArray(filters?.[filterKey]) 
      ? filters?.[filterKey]?.length > 0 
      : (filters?.[filterKey] && filters?.[filterKey] !== defaultVal);
      
    return (
      <div className="d-flex justify-content-between align-items-center w-100 pe-3">
        <span>{title}</span>
        {isActive && (
          <span
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setFilters(prev => ({ ...prev, [filterKey]: defaultVal }));
            }}
            style={{ fontSize: "12px", color: "#dc3545", cursor: "pointer", zIndex: 10 }}
          >
            Clear
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="filter-sidebar">
      <div className="filter-header">
        <span>All Filters</span>
        <Button
          onClick={handleResetFilters}
          aria-label="Reset Filters"
          title="Reset Filters"
          className="p-0 d-flex align-items-center gap-2 shadow-none"
          style={{ background: 'transparent', border: 'none', color: '#333' }}
        >
          <img src="/images/filter.svg" alt="Reset" style={{ width: '16px', height: '16px' }} />
          <span style={{ fontWeight: 400 }}>Reset Filters</span>
        </Button>
      </div>
      <Accordion alwaysOpen className="tender-accordion">
        <Accordion.Item eventKey="0">
          <Accordion.Header>{renderHeader("Status", "status")}</Accordion.Header>
          <Accordion.Body>
            {(filterData?.dynamic?.status || []).map(item => (
              <Form.Check 
                key={item} 
                label={item} 
                checked={(filters?.status || []).includes(item)}
                onChange={() => toggleSelection("status", item)} 
                style={{ marginBottom: '4px' }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>{renderHeader("Organization", "organization")}</Accordion.Header>
          <Accordion.Body>
            {renderSearchableList({ 
              data: filterData.dynamic.organization, 
              search: orgSearch, 
              setSearch: setOrgSearch, 
              filterKey: "organization" 
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2">
          <Accordion.Header>{renderHeader("Category", "category")}</Accordion.Header>
          <Accordion.Body>
            {renderSearchableList({ 
              data: filterData.dynamic.category, 
              search: catSearch, 
              setSearch: setCatSearch, 
              filterKey: "category" 
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="3">
          <Accordion.Header>{renderHeader("Department", "department")}</Accordion.Header>
          <Accordion.Body>
            {renderSearchableList({ 
              data: filterData.dynamic.department, 
              search: deptSearch, 
              setSearch: setDeptSearch, 
              filterKey: "department" 
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="4">
          <Accordion.Header>{renderHeader("Ministry", "ministry")}</Accordion.Header>
          <Accordion.Body>
            {renderSearchableList({ 
              data: filterData.dynamic.ministry, 
              search: minSearch, 
              setSearch: setMinSearch, 
              filterKey: "ministry" 
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="5">
          <Accordion.Header>{renderHeader("State", "state")}</Accordion.Header>
          <Accordion.Body>
            {renderSearchableList({ 
              data: filterData.dynamic.state, 
              search: stateSearch, 
              setSearch: setStateSearch, 
              filterKey: "state" 
            })}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="6">
          <Accordion.Header>{renderHeader("Submission Deadline", "deadline")}</Accordion.Header>
          <Accordion.Body>
            {(filterData.static.deadline || []).map(item => (
              <Form.Check 
                key={item.value} 
                label={item.label} 
                checked={(filters.deadline || []).includes(item.value)} 
                onChange={() => toggleSelection("deadline", item.value)} 
                style={{ marginBottom: '4px' }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="7">
          <Accordion.Header>{renderHeader("MSE / Startup Eligibility", "eligibility")}</Accordion.Header>
          <Accordion.Body>
            {(filterData.static.eligibility || []).map(item => (
              <Form.Check 
                key={item.value} 
                label={item.label} 
                checked={(filters.eligibility || []).includes(item.value)} 
                onChange={() => toggleSelection("eligibility", item.value)} 
                style={{ marginBottom: '4px' }}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="8">
          <Accordion.Header>{renderHeader("Tender Value Range", "minValue", 0)}</Accordion.Header>
          <Accordion.Body>
            <div className="slider-wrapper">
              <Form.Control 
                type="text" 
                value={(filters.minValue || 0).toLocaleString("en-IN")} 
                onChange={(e) => {
                  const val = Math.min(Number(e.target.value.replace(/[^0-9]/g, "")), 500000000);
                  setFilters(prev => ({ ...prev, minValue: val }));
                }} 
                className="mb-3" 
              />
              
              <div className="range-container">
                <input 
                  type="range" 
                  className="tender-range"
                  min={0} 
                  max={500000000} 
                  step={50000000} 
                  value={filters.minValue || 0}
                  onChange={(e) => setFilters(prev => ({ ...prev, minValue: Number(e.target.value) }))}
                  style={{
                    background: `linear-gradient(to right, #2a9d8f ${((filters.minValue || 0) / 500000000) * 100}%, #e0e0e0 0%)`
                  }}
                />
                
                <div className="range-ticks">
                  {[0, 100000000, 200000000, 300000000, 400000000, 500000000].map((val, i) => (
                    <span 
                      key={i} 
                      className="tick" 
                      style={{ left: `${(val / 500000000) * 100}%` }} 
                    />
                  ))}
                </div>
              </div>

              <div className="slider-labels">
                {[0, 100000000, 200000000, 300000000, 400000000, 500000000].map((val, i) => (
                  <span 
                    key={i} 
                    style={{ left: `${(val / 500000000) * 100}%` }}
                  >
                    {val === 0 ? "0" : `${val / 10000000} Cr`}
                  </span>
                ))}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="9">
          <Accordion.Header>{renderHeader("EMD Amount", "emdValue", 0)}</Accordion.Header>
          <Accordion.Body>
            <div className="slider-wrapper">
              <Form.Control 
                type="text" 
                value={(filters.emdValue || 0).toLocaleString("en-IN")} 
                onChange={(e) => {
                  const val = Math.min(Number(e.target.value.replace(/[^0-9]/g, "")), 5000000);
                  setFilters(prev => ({ ...prev, emdValue: val }));
                }} 
                className="mb-3 custom-input" 
              />

              <div className="range-container">
                <input 
                  type="range" 
                  className="tender-range"
                  min={0} 
                  max={5000000} 
                  step={500000} 
                  value={filters.emdValue || 0}
                  onChange={(e) => setFilters(prev => ({ ...prev, emdValue: Number(e.target.value) }))}
                  style={{
                    background: `linear-gradient(to right, #2a9d8f ${((filters.emdValue || 0) / 5000000) * 100}%, #e0e0e0 0%)`
                  }}
                />

                <div className="range-ticks">
                  {[0, 1000000, 2000000, 3000000, 4000000, 5000000].map((val, i) => (
                    <span key={i} className="tick" style={{ left: `${(val / 5000000) * 100}%` }} />
                  ))}
                </div>
              </div>

              <div className="slider-labels">
                {[0, 1000000, 2000000, 3000000, 4000000, 5000000].map((val, i) => (
                  <span key={i} style={{ left: `${(val / 5000000) * 100}%` }}>
                    {val === 0 ? "0" : `${val / 100000} L`}
                  </span>
                ))}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default FilterSidebar;